import React, { useEffect, useState } from "react";
import { Box, Dialog, Chip,  DialogTitle, DialogContent, IconButton, CircularProgress, Avatar, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import axios from "axios";
import HistoryIcon from "@mui/icons-material/History";
import { styled, useTheme, Theme, CSSObject } from "@mui/material/styles";

interface AuditLogEntry {
  id: number;
  action: "created" | "updated" | "deleted";
  user_name: string | null;
  changes: Record<string, { old: any; new: any }> | null; 
  created_at: string;
}

interface AuditTrailProps {
  open: boolean;
  onClose: () => void;
  auditableType: string; // "Expense" | "PropertyIncome" | "Deposit"
  auditableId: number;
  title?: string;
}

const FIELD_LABELS: Record<string, string> = {
  amount: "Amount",
  hst: "HST",
  total: "Total",
  status: "Status",
  category: "Category",
  description: "Description",
  date: "Date",
  return_amount: "Return amount",
};

const formatValue = (field: string, value: any) => {
  if (value === null || value === undefined || value === "") return "—";
  if (["amount", "hst", "total", "return_amount"].includes(field)) {
    return `$${Number(value).toFixed(2)}`;
  }
  return String(value);
};

const initials = (name: string | null) =>
  (name || "?")
    .split(" ")
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

const timeAgo = (dateStr: string) => {
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
};

const AuditTrail: React.FC<AuditTrailProps> = ({ open, onClose, auditableType, auditableId, title }) => {
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const API_URL = process.env.REACT_APP_API_URL;
  const theme = useTheme();

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    axios
      .get(`${API_URL}/audit-logs/${auditableType}/${auditableId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      })
      .then((res) => setLogs(res.data))
      .catch(() => setLogs([]))
      .finally(() => setLoading(false));
  }, [open, auditableType, auditableId]);

  const describe = (log: AuditLogEntry) => {
    if (log.action === "created") return `${log.user_name || "Someone"} created this record`;
    if (log.action === "deleted") return `${log.user_name || "Someone"} deleted this record`;
    const fields = Object.keys(log.changes || {}).map((f) => FIELD_LABELS[f] || f);
    return `${log.user_name || "Someone"} edited ${fields.join(", ") || "this record"}`;
  };

  return (
   <Dialog
  open={open}
  onClose={onClose}
  maxWidth="sm"
  fullWidth
  PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
>
  {/* Header */}
  <Box
    sx={{
     background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 55%, ${theme.palette.primary.light} 100%)`,
      px: 3,
      py: 2.5,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
    }}
  >
    <Box display="flex" alignItems="center" gap={1.4}>
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: "50%",
          backgroundColor: "rgba(255,255,255,0.16)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <HistoryIcon sx={{ color: "#fff", fontSize: 19 }} />
      </Box>
      <Box>
        <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "1rem", lineHeight: 1.2 }}>
          Activity History
        </Typography>
        {title && (
          <Typography sx={{ color: "rgba(255,255,255,0.75)", fontSize: "0.78rem", mt: 0.2 }}>
            {title}
          </Typography>
        )}
      </Box>
    </Box>
    <IconButton onClick={onClose} size="small" sx={{ color: "#fff" }}>
      <CloseIcon fontSize="small" />
    </IconButton>
  </Box>

  {/* Body */}
  <DialogContent sx={{ backgroundColor: "#fafafa", px: 3, py: 3 }}>
    {loading ? (
      <Box display="flex" justifyContent="center" py={5}>
        <CircularProgress size={24} />
      </Box>
    ) : logs.length === 0 ? (
      <Box textAlign="center" py={5}>
        <HistoryIcon sx={{ fontSize: 38, color: "#d0d0d0", mb: 1 }} />
        <Typography color="textSecondary" variant="body2">
          No history recorded yet.
        </Typography>
      </Box>
    ) : (
      <Box position="relative" sx={{ pl: 0.5 }}>
        {/* Timeline spine */}
        <Box
          sx={{
            position: "absolute",
            left: 17,
            top: 6,
            bottom: 6,
            width: "2px",
            backgroundColor: "#e3e3e8",
          }}
        />

        <Box display="flex" flexDirection="column" gap={2.5}>
          {logs.map((log) => {
            const style =
              log.action === "created"
                ? { color: "#2e7d32", bg: "#e8f5e9", border: "#a5d6a7" }
                : log.action === "deleted"
                ? { color: "#c62828", bg: "#fdecea", border: "#ef9a9a" }
                : { color: "#1565c0", bg: "#e3f2fd", border: "#90caf9" };

            return (
              <Box key={log.id} display="flex" gap={1.5} position="relative">
                <Avatar
                  sx={{
                    width: 36,
                    height: 36,
                    fontSize: 12,
                    fontWeight: 700,
                    bgcolor: style.bg,
                    color: style.color,
                    border: "2.5px solid #fafafa",
                    boxShadow: `0 0 0 2px ${style.border}`,
                    zIndex: 1,
                    flexShrink: 0,
                  }}
                >
                  {initials(log.user_name)}
                </Avatar>

                <Box
                  flex={1}
                  sx={{
                    backgroundColor: "#fff",
                    border: "1px solid #ececec",
                    borderRadius: 2,
                    px: 2,
                    py: 1.4,
                    boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
                  }}
                >
                  <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={1}>
                    <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.4 }}>
                      {describe(log)}
                    </Typography>
                    <Chip
                      label={log.action}
                      size="small"
                      sx={{
                        height: 19,
                        fontSize: "0.62rem",
                        fontWeight: 700,
                        textTransform: "capitalize",
                        backgroundColor: style.bg,
                        color: style.color,
                        flexShrink: 0,
                      }}
                    />
                  </Box>

                  <Typography variant="caption" color="textSecondary" sx={{ display: "block", mt: 0.4 }}>
                    {new Date(log.created_at).toLocaleString()} · {timeAgo(log.created_at)}
                  </Typography>

                  {log.action === "updated" && log.changes && (
                    <Box mt={1.2} display="flex" flexDirection="column" gap={0.7}>
                      {Object.entries(log.changes).map(([field, { old, new: next }]) => (
                        <Box
                          key={field}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: 0.7,
                            backgroundColor: "#fafafa",
                            border: "1px solid #f0f0f0",
                            borderRadius: 1.5,
                            px: 1,
                            py: 0.6,
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ color: "#757575", fontWeight: 600, minWidth: 70, fontSize: "0.68rem" }}
                          >
                            {FIELD_LABELS[field] || field}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              textDecoration: "line-through",
                              color: "#c62828",
                              backgroundColor: "#fdecea",
                              borderRadius: "4px",
                              px: 0.7,
                              py: 0.1,
                            }}
                          >
                            {formatValue(field, old)}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#bdbdbd" }}>→</Typography>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#2e7d32",
                              backgroundColor: "#e8f5e9",
                              borderRadius: "4px",
                              px: 0.7,
                              py: 0.1,
                              fontWeight: 600,
                            }}
                          >
                            {formatValue(field, next)}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    )}
  </DialogContent>
</Dialog>
  );
};

export default AuditTrail;
